import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { change, Field } from "redux-form";

import {
	Icon,
	ReduxControls,
	Tooltip
} from 'rhombus-react';

import {
	useClickOutsideHandler,
	useModalStack
} from 'utils/common';
import { ASSET_TYPES } from 'utils/constants';

import SelectAssets from "widgets/modals/assets/SelectAssets";

import {
	getModalProps,
	getSelectProps,
	mapAssetToVersionVariable,
	sanitizeVariableFieldName
} from './Helpers';
import IconCell from "./IconCell";
import VariableInfoCell from './VariableInfoCell';

import './AssetEditor.css';
import "./TextEditor.css";

const TWO = 2
	, CLOUD_ICON = "far fa-cloud"
	;


export default function TextEditor({ libraryId, search, form, value = {}, disabledTextAssets }){
	const dispatch = useDispatch()
		, [isEditing, setIsEditing] = useState(false)
		, [isFormatting, setIsFormatting] = useState(value.isFormatted)
		, [isSearching, setIsSearching] = useState(false)
		, isExistingAsset = useMemo(() => (value.type === ASSET_TYPES.TEXT && !!value.assetId), [value])
		, selectProps = getSelectProps({ search, name:value.name, type:ASSET_TYPES.TEXT })
		, commonModalProps = getModalProps({ assetType:value.type, closeModal })
		// eslint-disable-next-line no-unused-vars
		, [modalStack, { pushModal, clearStack }, ModalStackHead] = useModalStack({ commonModalProps })
		, fieldName = sanitizeVariableFieldName(value.name)
		, inputName = `${fieldName}Formatted`
		, [rowRef] = useClickOutsideHandler(returnToSummary)
		;

	// TODO: necessary?
	if(!value || typeof(value) !== "object"){ return null; }

	useEffect(forceInputChange, [value]);

	function closeModal(){
		clearStack();
	}
	function onInputChange(data){
		let newValue = { ...value, data, isFormatted: (isExistingAsset && data !== value.prop) };
		dispatch(change(form, fieldName, newValue));
		// do not change isFormatting so we don't automatically leave formatting mode when updating formatting
	}
	function onSelectChange(newValue){
		setIsFormatting(false);
	}
	function forceInputChange(newValue = value){
		let inputValue = newValue.data || "";
		dispatch(change(form, inputName, inputValue));
	}
	function startEdit(){
		setIsEditing(true);
		setIsSearching(false);
		setIsFormatting(value.isFormatted);
	}
	function startSearch(){
		setIsEditing(true);
		setIsSearching(true);
		setIsFormatting(false);
	}
	function addFormatting(){
		setIsSearching(false);
		setIsFormatting(true);
	}
	function removeFormatting(){
		let newValue = { ...value, data: value.prop, isFormatted:false };
		dispatch(change(form, fieldName, newValue));
		setIsFormatting(false);
	}
	function removeAsset(){
		let newValue = {
			name: value.name,
			data:"",
			prop:"",
			isFormatted:false,
			type:ASSET_TYPES.TEXT,
		};
		dispatch(change(form, fieldName, newValue));
		setIsFormatting(false);
		setIsSearching(false);
	}
	function returnToSummary(){
		setIsEditing(false);
		setIsSearching(false);
		setIsFormatting(value.isFormatted);
	}

	function selectFromModal([assetId], assets){
		let selectedAsset = assets.find(asset => asset.id === assetId);
		if(!selectedAsset){
			// This generally shouldn't happen because we flush selections when the Asset items change
			console.error("Could not update asset selection.  Asset not found.");
			return;
		}
		let newValue = mapAssetToVersionVariable(value.name, selectedAsset);
		dispatch(change(form, fieldName, newValue));
	}
	const openSelectionModal = () => {
		function Configured(props){
			return <SelectAssets {...props} type = {value.type.toLowerCase()} onSelect = {selectFromModal} />;
		}
		pushModal(Configured);
	};

	function Input(){
		let description = "Use formatting if you wish for the text to be displayed in the version to be different than that of the text asset while still being linked to the asset."
			, tip = !isExistingAsset ? null : <Tooltip description = {description} direction = "right" size = "large" />
			, labelText = isExistingAsset ? "Formatting" : value.name
			, { fieldProps = {} } = value
			, charCount = (
				<div className = "text-label-extra">
					<small>{`${value.data?.length || 0} characters`}</small>
				</div>
			)
			, compProps = {
				className: "text-asset-field text-asset-input",
				component: ReduxControls.Input,
				name:      inputName,
				label:     <><div>{labelText}{tip}</div>{charCount}</>,
				onChange:  onInputChange,
				...fieldProps,
			}
			;

		return <Field {...compProps} autoFocus />;
	}

	function Search(){
		return (
			<Field
				{...selectProps}
				className   = "text-asset-field text-asset-select"
				name        = {fieldName}
				isClearable = {false}
				onChange    = {onSelectChange}
			/>
		);
	}
	function SearchButtons(){
		return (
			<td className = {`controls-cell search-controls ${value.label ? "" : "disabled"}`}>
				<div className = "buttons">
					<Tooltip description = "Remove text asset" direction = "left">
						<Icon
							className        = "bounce-circle"
							name             = "far fa-times-circle"
							onClick          = {removeAsset}
						/>
					</Tooltip>
					<Tooltip description = {`${isFormatting ? "Remove" : "Apply"} text asset formatting`} direction = "left">
						<Icon
							className  = "bounce-circle"
							name       = {`far fa-text${isFormatting ? "-slash" : ""}`}
							onClick    = {isFormatting ? removeFormatting : addFormatting}
						/>
					</Tooltip>
				</div>
			</td>
		);
	}

	function Editor(){
		let showSelect = isExistingAsset || isSearching;
		return (
			<>
				<td className = {`edit-version-main editCell ${isFormatting ? "formatting" : ""}`} colSpan = {showSelect ? 1 : TWO} >
					{/* show search when search icon is clicked, and when editing existing assets */}
					{showSelect && Search()}
					{/* show input when editing freeform assets, and when formatting existing assets */}
					{(!showSelect || isFormatting) && Input()}
				</td>
				{showSelect && SearchButtons()}
			</>
		);
	}

	function Summary(){
		let requiredIcon = undefined;
		if (value?.fieldProps?.required) {
			requiredIcon = value.data ? "fas fa-check" : "fas fa-star";
		}

		return (
			<>
				<VariableInfoCell
					name = {value.name}
					data = {value.data}
					onClick = {startEdit}
					requiredIcon = {requiredIcon}
					iconName = {isExistingAsset && CLOUD_ICON} />
				<td className = "controls-cell">
					<div className = "buttons">
						<Tooltip description = "Edit" direction = "top">
							<Icon className = "bounce-circle cypress-edit-text" name = "edit" onClick = {startEdit} />
						</Tooltip>
						{ !disabledTextAssets && <Tooltip description = "Search Text Assets" direction = "top">
							<Icon className = "bounce-circle cypress-search-text" name = "search" iconStyle = "far" onClick = {startSearch} />
						</Tooltip> }
						{ !disabledTextAssets && <Tooltip description = "Select Text" direction = "top">
							<Icon className = "bounce-circle cypress-select-text" name = {CLOUD_ICON} onClick = {openSelectionModal} />
						</Tooltip> }
					</div>
				</td>
			</>
		);
	}

	const Body = useMemo(() => {
		return isEditing ? Editor() : Summary();
	}, [value, selectProps]);

	return (
		<>
			{ModalStackHead && <ModalStackHead />}
			<tr className = {`edit-version-table-row text-asset-row ${isEditing ? "is-editing" : ""}`} ref={rowRef} >
				<IconCell iconName = "assetText" onClick = {startEdit} />
				{Body}
			</tr>
		</>
	);
}
